import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface SheetProps {
    title: string;
    doneLabel: string;
    onClose(): void;
    children: ReactNode;
}

// A bottom sheet on phones and a centred dialog on wide screens. Closes on
// Escape, on the scrim and on the done button; focus moves into the sheet and
// returns to where it came from.
export function Sheet({ title, doneLabel, onClose, children }: SheetProps) {
    const sheetRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const previous = document.activeElement as HTMLElement | null;
        sheetRef.current?.querySelector<HTMLElement>('button, input, select')?.focus();
        return () => previous?.focus?.();
    }, []);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    return createPortal(
        <div className="sheet-layer">
            <div className="sheet-scrim" onClick={onClose} data-testid="sheet-scrim" />
            <div ref={sheetRef} role="dialog" aria-modal="true" aria-label={title} className="sheet">
                <div className="sheet-top">
                    <h2 className="sheet-title">{title}</h2>
                    <button type="button" className="sheet-done" onClick={onClose}>{doneLabel}</button>
                </div>
                <div className="sheet-body">{children}</div>
            </div>
        </div>,
        document.body,
    );
}
